import { Input, Typography } from '@breetometer/components';
import { InfoIcon } from 'BreetAssets';
import { Tooltip, TooltipContent } from 'BreetComponents';
import { showToast } from 'BreetHelpers';
import { kybAuthAction, openModal, useAppDispatch } from 'BreetRedux';
import { type ChangeEvent, type DragEvent, forwardRef, useCallback, useRef, useState } from 'react';

import { getAcceptedFileTypes, getPreviewFileMeta, type IFileInput } from './FileInput.helpers';
import FileSelect from './select';

const FileInput = forwardRef<HTMLInputElement, IFileInput>(
	({ label, errorMsg, accept, name, value, onChange, id, tooltip }, ref) => {
		const fileInputRef = useRef<HTMLInputElement | null>(null);

		const { setPreviewFile } = kybAuthAction;

		const dispatch = useAppDispatch();

		const [isDragging, setIsDragging] = useState(false);

		const mainRef = ref ?? fileInputRef;

		const triggerFileSelect = useCallback(() => {
			if (typeof mainRef === 'function') return;
			mainRef.current?.click();
		}, [mainRef]);

		const { mime, acceptedMimeTypes } = getAcceptedFileTypes(accept);

		const prepareFilePreview = useCallback(
			(valueArg: string | FileList | undefined) => {
				if (valueArg) {
					const previewFile = getPreviewFileMeta(valueArg, id);
					dispatch(setPreviewFile(previewFile));
				}
			},
			[dispatch, id, setPreviewFile]
		);

		const handleFileChange = useCallback(
			(e: ChangeEvent<HTMLInputElement>) => {
				const file = e.target.files ?? null;
				if (!file?.length) return;
				onChange(file);
				prepareFilePreview(file);
			},
			[prepareFilePreview, onChange]
		);

		// The hidden native input can't receive drops, so the wrapper is the drop target. dragover fires
		// continuously while hovering, so setting the flag there (idempotent) avoids child-boundary flicker.
		const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
			e.preventDefault();
			setIsDragging(true);
		}, []);

		const handleDragLeave = useCallback(() => setIsDragging(false), []);

		const handleDrop = useCallback(
			(e: DragEvent<HTMLElement>) => {
				e.preventDefault();
				setIsDragging(false);

				const { files } = e.dataTransfer;
				if (!files.length) return;

				const dropped = files[0];
				// Enforce the same accepted types as the click path's `accept` filter (the schema only
				// validates size, not type). Unlike a rejected file dialog, a rejected drop needs to say why.
				if (mime && !mime.split(',').some((type) => type.trim() === dropped.type)) {
					showToast('error', `Please upload a ${acceptedMimeTypes} file`);
					return;
				}

				// Normalise a multi-file drop to the single FileList the consumers (and schema) expect.

				const transfer = new DataTransfer();
				transfer.items.add(dropped);
				onChange(transfer.files);
				prepareFilePreview(transfer.files);
			},
			[mime, acceptedMimeTypes, onChange, prepareFilePreview]
		);

		const onPreview = useCallback(() => {
			if (value) {
				prepareFilePreview(value);
				dispatch(
					openModal({
						id: 'previewKYBFile',
					})
				);
			}
		}, [dispatch, prepareFilePreview, value]);

		return (
			<div className='breet_kyb_form_file_input'>
				<div className='label-section'>
					<label htmlFor={id}>{label}</label>
					{tooltip && (
						<Tooltip tipElement={<TooltipContent content={tooltip} />}>
							<InfoIcon className='info-icon svg-dark' />
						</Tooltip>
					)}
				</div>

				<Typography
					as={value ? 'div' : 'button'}
					className={`breet_kyb_form_file_input_wrapper bg-primary-600 border-dark-navy${isDragging ? ' is-dragging' : ''}`}
					onClick={value ? undefined : triggerFileSelect}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					<div className='breet_kyb_form_file_input_wrapper_input'>
						<Input
							type='file'
							label=''
							id={id}
							accept={mime}
							ref={mainRef}
							name={name}
							onChange={handleFileChange}
						/>
					</div>

					<FileSelect
						acceptedMimeTypes={acceptedMimeTypes}
						value={value}
						onPreview={onPreview}
						onReplace={triggerFileSelect}
					/>
				</Typography>

				{!!errorMsg && (
					<div className='breet_kyb_form_file_input_error_message'>
						<Typography
							as='p'
							variant='sub_head'
							className='bt_input-error-message'
						>
							{errorMsg}
						</Typography>
					</div>
				)}
			</div>
		);
	}
);

export default FileInput;

FileInput.displayName = 'BreetFileInput';
